import { FactiiiRequirement } from '@shared/all/domains/factiiis';

import type { AddFactiiiState } from './formState';
import type { SectionKey } from './sections';

/** The requirement half of the form — everything a template decides for you. */
type TemplateSeed = Pick<
  AddFactiiiState,
  | 'dataRequirement'
  | 'locationRequirement'
  | 'needsTime'
  | 'extraRequirements'
  | 'botEnabled'
>;

export type TemplateIconName =
  | 'BookOpen'
  | 'Bot'
  | 'CalendarClock'
  | 'Camera'
  | 'FileText'
  | 'FolderSearch'
  | 'Hash'
  | 'MapPin'
  | 'SlidersHorizontal'
  | 'Tag';

export type FactiiiTemplate = {
  key: string;
  title: string;
  blurb: string;
  icon: TemplateIconName;
  /** Which block of the gallery it sits in. PRESET never renders there: a
   *  caller lands on it by `templateKey`, because on its own it makes no sense
   *  (a branch is only meaningful hung under a parent). */
  section: 'COMMON' | 'AI' | 'PRESET';
  seed: TemplateSeed;
  namePlaceholder: string;
  descriptionLabel?: string;
  descriptionPlaceholder?: string;
  /** Settings lifted out of Options onto the form itself, because they are what
   *  this template is for — an AI-replies factiii that never mentions a model
   *  until you go hunting is the same factiii as any other. */
  highlight?: SectionKey[];
};

const NOTHING: TemplateSeed = {
  dataRequirement: null,
  locationRequirement: null,
  needsTime: false,
  extraRequirements: [],
  botEnabled: false
};

/**
 * A template is a prefill and nothing more: it seeds the same form every other
 * template lands on, and every option stays reachable whichever one you pick.
 * Custom is simply the seed that fills in nothing.
 *
 * Ordered as the gallery renders them. Each seed is applied whole, so switching
 * template never leaves a stray requirement from the previous pick behind.
 */
export const FACTIII_TEMPLATES: FactiiiTemplate[] = [
  {
    key: 'custom',
    title: 'Custom',
    blurb: 'Start empty and fill in your own.',
    icon: 'SlidersHorizontal',
    section: 'COMMON',
    seed: NOTHING,
    namePlaceholder: 'e.g. Road Closure'
  },
  {
    key: 'simple',
    title: 'Simple tag',
    blurb: 'Just a label, nothing required.',
    icon: 'Tag',
    section: 'COMMON',
    seed: NOTHING,
    namePlaceholder: 'e.g. Recipes'
  },
  {
    key: 'place',
    title: 'Place',
    blurb: 'Every tag records a place.',
    icon: 'MapPin',
    section: 'COMMON',
    seed: {
      ...NOTHING,
      locationRequirement: FactiiiRequirement.LOCATION_REQUIRED
    },
    namePlaceholder: 'e.g. Visited',
    highlight: ['place']
  },
  {
    key: 'datetime',
    title: 'Date & time',
    blurb: 'Every tag records a date.',
    icon: 'CalendarClock',
    section: 'COMMON',
    seed: { ...NOTHING, needsTime: true },
    namePlaceholder: 'e.g. Deadline',
    highlight: ['place']
  },
  {
    key: 'number',
    title: 'Number',
    blurb: 'Every tag carries a number to total.',
    icon: 'Hash',
    section: 'COMMON',
    seed: { ...NOTHING, dataRequirement: FactiiiRequirement.INTEGER_REQUIRED },
    namePlaceholder: 'e.g. Calories',
    highlight: ['value']
  },
  {
    key: 'note',
    title: 'Note',
    blurb: 'Every tag carries a written detail.',
    icon: 'FileText',
    section: 'COMMON',
    seed: { ...NOTHING, dataRequirement: FactiiiRequirement.DATA_REQUIRED },
    namePlaceholder: 'e.g. Serial Number',
    highlight: ['value']
  },
  {
    key: 'photo',
    title: 'Photo or video',
    blurb: 'The post has to show it.',
    icon: 'Camera',
    section: 'COMMON',
    seed: {
      ...NOTHING,
      extraRequirements: [FactiiiRequirement.MEDIA_REQUIRED]
    },
    namePlaceholder: 'e.g. Pothole',
    highlight: ['post']
  },
  {
    key: 'source',
    title: 'Source',
    blurb: 'Every tag cites a link.',
    icon: 'BookOpen',
    section: 'COMMON',
    seed: {
      ...NOTHING,
      extraRequirements: [FactiiiRequirement.SOURCE_REQUIRED]
    },
    namePlaceholder: 'e.g. Study Cited',
    highlight: ['post']
  },
  {
    key: 'root',
    title: 'AI file tag',
    blurb: 'AI tags your files with it.',
    icon: 'FolderSearch',
    section: 'AI',
    seed: { ...NOTHING, extraRequirements: [FactiiiRequirement.ROOT] },
    namePlaceholder: 'e.g. Medical',
    descriptionLabel: 'The rule — when should AI apply this?',
    descriptionPlaceholder:
      'e.g. The document reports a lab result for a blood test',
    // The other requirements are the tools the file pass reads off a document,
    // so a root is half-configured until you have seen them.
    highlight: ['value', 'place']
  },
  {
    // The root template minus ROOT: the vault's "Sub factiii" button creates
    // one and hangs it under the parent, so the file tagger asks about it only
    // once the parent fired (.specs/factiiis.md → The tree).
    key: 'sub',
    title: 'Sub factiii',
    blurb: 'AI asks about it only after its parent fires.',
    icon: 'FolderSearch',
    section: 'PRESET',
    seed: NOTHING,
    namePlaceholder: 'e.g. MRI',
    descriptionLabel: 'The rule — when should AI apply this?',
    descriptionPlaceholder: 'e.g. The study was an MRI',
    highlight: ['value', 'place']
  },
  {
    key: 'bot',
    title: 'AI replies',
    blurb: 'A bot answers every post.',
    icon: 'Bot',
    section: 'AI',
    seed: { ...NOTHING, botEnabled: true },
    namePlaceholder: 'e.g. Fact Check',
    highlight: ['bot']
  }
];

/** What a caller that skips the gallery (a board list) lands on. */
export const CUSTOM_TEMPLATE = FACTIII_TEMPLATES.find(
  (template) => template.key === 'custom'
)!;
